/**
 * Customer profile.
 *
 * Endpoints (verified against /openapi.json):
 *   GET   /api/v1/customer/profile/          → my profile (incl. isProfileComplete flag)
 *   POST  /api/v1/customer/profile/complete  → complete the profile (PAN, DOB, customer_type, etc.)
 *   PATCH /api/v1/customer/profile/update    → partial update; OTP challenge when
 *                                              mobile_e164 / email / pan changes
 */
import { useMutation, useQuery, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { createKeyFactory, type Schemas } from '@finmin/api-client';
import type { CustomerPAN } from './customer-pans';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';

// ---------- types -----------------------------------------------------------

/**
 * Customer profile shape — augmented locally so we don't have to wait on the
 * OpenAPI generator to publish the v2 fields. When `pnpm gen:types` catches
 * up to the new backend, we can drop the intersection and rely on Schemas[...]
 * directly. Until then, every consumer of `CustomerProfileResponse` sees the
 * v2 fields without a cast.
 */
export type CustomerProfileResponse = Omit<
  Schemas['CustomerProfileResponse'],
  'customer_type'
> & {
  customer_type?: 'salaried' | 'professional' | 'business_owner' | 'others' | string | null;
  /** v2: free-text describing `entity_structure === 'other'`. */
  entity_structure_other_detail?: string | null;
  /** v2: free-text describing `services_required` containing `'any_other'`. */
  service_other_detail?: string | null;
  /** v2: the `others` branch's category selector. */
  other_customer_category?: string | null;
  other_customer_category_detail?: string | null;
  /** v2: backend now exposes the questionnaire `answers` blob too. */
  answers?: Record<string, unknown> | null;
  /**
   * Sum of `money_saved` across the customer's completed (active) service
   * requests, as an INR string (e.g. "25000.00", "0.00"). `null`/absent until
   * the backend populates it.
   */
  total_money_saved?: string | null;
  /** v2 multi-PAN: every PAN this customer has registered. */
  pans: CustomerPAN[];
};

export type ProfileCompleteRequest = Schemas['ProfileCompleteRequest'];

/**
 * Partial-update payload for PATCH /profile/update.
 *
 * Declared locally because the OpenAPI generator may not yet expose this shape
 * (the backend just added the endpoint). Fields are intentionally a loose
 * superset — backend rejects unknown fields per spec, so the form is the place
 * to constrain what we send.
 */
export interface ProfileUpdateRequest {
  full_name?:        string;
  mobile_e164?:      string;
  email?:            string;
  dob?:              string;
  customer_password?: string;
  customer_type?:    string;
  // Branch-specific fields — partial superset matching schema.ts
  annual_income_range?:         string;
  annual_turnover_range?:       string;
  entity_structure?:            string;
  entity_structure_other_detail?: string;
  income_exposures?:            string[];
  other_exposure_detail?:       string;
  services_required?:           string[];
  service_other_detail?:        string;
  other_customer_category?:     string;
  other_customer_category_detail?: string;
  /** OTP code — present only on the verification re-submit. */
  otp?: string;
}

/**
 * Discriminated union for /profile/update.
 *
 *   Challenge:  { otp_required: true,  updated: false, exp, otp_target, profile: null }
 *   Completed:  { otp_required: false, updated: true,  exp: null, otp_target: null, profile: CustomerProfileResponse }
 *
 * Callers (the OTP-protected update hook) narrow on `otp_required`.
 */
export type ProfileUpdateResponse =
  | {
      otp_required: true;
      updated:      false;
      exp:          number;
      otp_target:   string | null;
      profile:      null;
    }
  | {
      otp_required: false;
      updated:      true;
      exp:          null;
      otp_target:   null;
      profile:      CustomerProfileResponse;
    };

// ---------- query keys ------------------------------------------------------

export const profileKeys = createKeyFactory('customer.profile', {
  me: () => ['me'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/profile/ — returns the customer profile + isProfileComplete flag. */
export async function getProfile(signal?: AbortSignal) {
  const { data } = await apiClient.get<CustomerProfileResponse>('/api/v1/customer/profile/', {
    signal,
  });
  return data;
}

/** POST /api/v1/customer/profile/complete — submit PAN/DOB/etc. to complete onboarding. */
export async function completeProfile(payload: ProfileCompleteRequest) {
  const { data } = await apiClient.post<CustomerProfileResponse>(
    '/api/v1/customer/profile/complete',
    payload,
  );
  return data;
}

/**
 * PATCH /api/v1/customer/profile/update — partial update.
 *
 * The same endpoint is both the challenge and the verification:
 *   • First call with sensitive field changes returns `otp_required: true`.
 *   • Caller re-sends the EXACT SAME payload plus `otp` to finalize.
 *   • Non-sensitive changes complete in one call.
 */
export async function updateProfile(payload: ProfileUpdateRequest) {
  const { data } = await apiClient.patch<ProfileUpdateResponse>(
    '/api/v1/customer/profile/update',
    payload,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useProfile(
  options?: Omit<UseQueryOptions<CustomerProfileResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CustomerProfileResponse, ApiError>({
    queryKey: profileKeys.me(),
    queryFn: ({ signal }) => getProfile(signal),
    ...options,
  });
}

export function useCompleteProfile(
  options?: UseMutationOptions<CustomerProfileResponse, ApiError, ProfileCompleteRequest>,
) {
  return useMutation<CustomerProfileResponse, ApiError, ProfileCompleteRequest>({
    mutationFn: completeProfile,
    // Spread caller's options FIRST, then override `onSuccess` with our own
    // chained version. If we did `...options` last (as before) it would
    // wipe out the cache invalidation, leaving useProfile() reading a stale
    // `isProfileComplete: false`. The next protected page would then bounce
    // the user straight back to /questionnaire.
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      // 1. Seed the cache with the fresh profile right away so any consumer
      //    re-rendering immediately (the _protected layout, in particular)
      //    sees `isProfileComplete: true` synchronously — no race with the
      //    background refetch, no redirect-loop flash.
      queryClient.setQueryData(profileKeys.me(), data);
      // 2. Mark the query stale so any other consumers refetch in the bg.
      await queryClient.invalidateQueries({ queryKey: profileKeys.me() });
      // 3. Forward to the caller's onSuccess (toast + navigate, etc).
      await options?.onSuccess?.(data, variables, context, mutation);
    },
  });
}

/**
 * useUpdateProfile — PATCH wrapper for /profile/update.
 *
 * Returns a discriminated union (otp_required true/false). The consumer
 * (the OTP-protected update hook in features/profile) decides whether to
 * open the OTP dialog or treat the result as a completed update.
 *
 * On `updated: true`, we seed the profile cache with the refreshed profile
 * so the page re-renders against fresh data immediately.
 */
export function useUpdateProfile(
  options?: UseMutationOptions<ProfileUpdateResponse, ApiError, ProfileUpdateRequest>,
) {
  return useMutation<ProfileUpdateResponse, ApiError, ProfileUpdateRequest>({
    mutationFn: updateProfile,
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      if (data.updated && data.profile) {
        queryClient.setQueryData(profileKeys.me(), data.profile);
        await queryClient.invalidateQueries({ queryKey: profileKeys.me() });
      }
      await options?.onSuccess?.(data, variables, context, mutation);
    },
  });
}
